var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { useUXPContext } from '@core/context/useUXPContext';
import useDebounce from '@hooks/useDebounce';
import { FontAwesomeIconWrapper } from '@icon/fontawesome/FontAwesomeIconWrapper';
import classNames from 'classnames';
import Button from '@widget/buttons/Button';
import { SkeletonLoader } from '@widget/loaders';
import NotificationBlock from '@helper/NotificationBlock';
import Input from '../Input';
import useInfiniteScroll from '@hooks/useInfiniteScroll';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import MediaPickCard from './MediaPickCard';
import MobileSheet from '@widget/popups/MobileSheet';
import { fetchMyMedia, fetchMediaFolders, fetchMediaTags, mediaItemUrl, libraryTypes } from './mediaLibrary';
import MediaLibraryTree, { mediaSelectionQuery, defaultMediaSelection } from './MediaLibraryTree';
/**
 * @export
 * A library card's icon for a file that isn't a picture, by media type
 */
export const MEDIA_TYPE_ICONS = {
    Image: 'fal image', Document: 'fal file-lines', Video: 'fal film', Audio: 'fal music',
};
const ITEMS_PER_PAGE = 24;
const SKELETON_TILES = 12;
/**
 * @export
 * The library's type filter value for "every type".
 */
export const LIBRARY_ALL_TYPES = 'all';
const ALL = LIBRARY_ALL_TYPES;
/**
 * @export
 * The caller's own uploads, server-paged with infinite scroll.
 */
const LibraryView = (props) => {
    var _a, _b, _c, _d, _e;
    const { show, mediaTypes, onPick, isSelected, onToggle, activeValue, onQueryChange, onTypeFilterChange, hideFilters, onItemsChange, grid, onTreeOpenChange, onFilteredChange, onTreeChange, onSelectionLabelChange, } = props;
    const context = useUXPContext();
    // Controlled when the host passes a value, else kept here
    const [ownQuery, setOwnQuery] = React.useState('');
    const [ownTypeFilter, setOwnTypeFilter] = React.useState(ALL);
    const query = (_a = props.query) !== null && _a !== void 0 ? _a : ownQuery;
    const typeFilter = (_b = props.typeFilter) !== null && _b !== void 0 ? _b : ownTypeFilter;
    function setQuery(value) {
        setOwnQuery(value);
        onQueryChange === null || onQueryChange === void 0 ? void 0 : onQueryChange(value);
    }
    function setTypeFilter(value) {
        setOwnTypeFilter(value);
        onTypeFilterChange === null || onTypeFilterChange === void 0 ? void 0 : onTypeFilterChange(value);
    }
    // Folders and tags (5.2 servers): a tree beside the cards. Older servers answer the
    // folders call with an error, and the view stays the flat list of all the user's files.
    const isMobile = useIsMobileLayout();
    const [folders, setFolders] = React.useState([]);
    const [tags, setTags] = React.useState([]);
    const [hasTree, setHasTree] = React.useState(false);
    // The first page waits for the folders answer, so it isn't fetched twice
    const [treeChecked, setTreeChecked] = React.useState(false);
    const [treeMode, setTreeMode] = React.useState('folders');
    const [selection, setSelection] = React.useState({ kind: 'root' });
    const [ownTreeSheet, setOwnTreeSheet] = React.useState(false);
    const treeSheet = (_c = props.treeOpen) !== null && _c !== void 0 ? _c : ownTreeSheet;
    function setTreeSheet(open) {
        setOwnTreeSheet(open);
        onTreeOpenChange === null || onTreeOpenChange === void 0 ? void 0 : onTreeOpenChange(open);
    }
    const selectionKey = JSON.stringify(selection);
    React.useEffect(() => {
        if (!show)
            return;
        let alive = true;
        Promise.all([fetchMediaFolders(context), fetchMediaTags(context)]).then(([f, t]) => {
            if (!alive)
                return;
            setHasTree(!f.error);
            setFolders(f.items);
            setTags(t.items);
            // Opens on the folder uploads go to
            const start = defaultMediaSelection(f.items);
            if (start)
                setSelection(start);
            setTreeChecked(true);
        });
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);
    const [items, setItems] = React.useState([]);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const debouncedQuery = useDebounce(query, 300);
    // Only the newest request may write state — an older page landing late
    // would otherwise overwrite the current filter's results.
    const requestId = React.useRef(0);
    // 'Icon' is a picker type, not a stored one: font icons come from IconsView
    // and uploaded icon files are recorded as Image.
    // Keyed on the joined string — callers pass `mediaTypes` as an inline array,
    // so its identity changes every render and can't drive the load effect.
    const typesKey = (mediaTypes || []).join(',');
    const queriedTypes = React.useMemo(() => libraryTypes(mediaTypes), 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typesKey]);
    const activeTypes = typeFilter === ALL ? queriedTypes : [typeFilter];
    const { ref: resultsRef, onScroll: onResultsScroll } = useInfiniteScroll({
        loading,
        hasMore: !error && items.length < total,
        count: items.length,
        resetKey: `${debouncedQuery}|${typeFilter}|${typesKey}|${selectionKey}|${hasTree}`,
        onLoadMore: () => load(items.length),
    });
    React.useEffect(() => {
        if (!show || !treeChecked)
            return;
        load(0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, treeChecked, debouncedQuery, typeFilter, typesKey, selectionKey, hasTree]);
    // Nothing in flight may write state once this view is gone — switching
    // source unmounts it outright.
    React.useEffect(() => () => { requestId.current++; }, []);
    function load(offset) {
        return __awaiter(this, void 0, void 0, function* () {
            const id = ++requestId.current;
            setLoading(true);
            const { items: rows, total: count, error: loadError } = yield fetchMyMedia(context, Object.assign({ max: ITEMS_PER_PAGE, last: offset, q: debouncedQuery, types: activeTypes }, (hasTree ? mediaSelectionQuery(selection) : {})));
            if (id !== requestId.current)
                return;
            setError(loadError ? libraryErrorMessage(loadError) : null);
            setItems(prev => offset > 0 ? [...prev, ...rows] : rows);
            setTotal(loadError ? 0 : count);
            setLoading(false);
        });
    }
    // Anything but the folder the library opens on counts as filtered
    React.useEffect(() => {
        const start = defaultMediaSelection(folders) || { kind: 'root' };
        onFilteredChange === null || onFilteredChange === void 0 ? void 0 : onFilteredChange(hasTree && JSON.stringify(start) !== selectionKey);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasTree, folders, selectionKey]);
    // The host looks up the previewed row's details from these
    React.useEffect(() => {
        onItemsChange === null || onItemsChange === void 0 ? void 0 : onItemsChange(items);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items]);
    // The generic text, with the server's reason after it when there is one
    function libraryErrorMessage(loadError) {
        const base = context.$L('uxp-core.media-picker.library-error');
        const reason = typeof loadError === 'string' ? loadError : loadError === null || loadError === void 0 ? void 0 : loadError.message;
        return typeof reason === 'string' && reason ? `${base}: ${reason}` : base;
    }
    // Phones, multiple mode: once a card here is ticked, a tap ticks (hold starts it)
    const selectMode = isMobile && !!onToggle && items.some(item => !!(isSelected === null || isSelected === void 0 ? void 0 : isSelected(mediaItemUrl(context, item.Path))));
    function renderItem(item) {
        const url = mediaItemUrl(context, item.Path);
        const isImage = item.MediaType === 'Image';
        // A card in the grid, or the compact card one below the other beside the preview; a click previews it
        return _jsx(MediaPickCard, { layout: grid ? 'vertical' : 'horizontal', image: isImage ? url : undefined, icon: isImage ? undefined : (MEDIA_TYPE_ICONS[item.MediaType] || 'fal file'), title: item.Name, description: String(item.IsOwner) === '0' ? item.OwnerName : item.Description, selected: !!(isSelected === null || isSelected === void 0 ? void 0 : isSelected(url)), onToggle: onToggle ? () => onToggle(url) : undefined, active: !!activeValue && activeValue === url, onPreview: () => onPick(url), isMobile: isMobile, selectMode: selectMode }, item.Key || url);
    }
    const tree = hasTree && _jsx(MediaLibraryTree, { mode: treeMode, onModeChange: setTreeMode, folders: folders, tags: tags, selection: selection, onSelect: (sel) => { setSelection(sel); setTreeSheet(false); } });
    const selectionLabel = selection.kind === 'root' ? context.$L('uxp-core.media-library.my-media')
        : selection.kind === 'shared' ? context.$L('uxp-core.media-library.shared-with-me')
            : selection.kind === 'folder' ? (((_d = folders.find(f => String(f.Key) === selection.key)) === null || _d === void 0 ? void 0 : _d.Name) || '')
                : (((_e = tags.find(t => String(t.Key) === selection.key)) === null || _e === void 0 ? void 0 : _e.Name) || '');
    React.useEffect(() => { onTreeChange === null || onTreeChange === void 0 ? void 0 : onTreeChange(hasTree); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasTree]);
    React.useEffect(() => { onSelectionLabelChange === null || onSelectionLabelChange === void 0 ? void 0 : onSelectionLabelChange(selectionLabel); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectionLabel]);
    const typeOptions = [
        { label: context.$L('uxp-core.media-picker.type-all'), value: ALL },
        ...queriedTypes.map(t => ({ label: context.$L(`uxp-core.media-picker.type-${t.toLowerCase()}`), value: t })),
    ];
    const filtered = !!debouncedQuery || typeFilter !== ALL;
    // Skeleton tiles only while the first page loads; later pages append below
    const firstLoad = loading && items.length === 0 && !error;
    // Root-relative on purpose: the picker runs inside the app. This view only mounts when the
    // library source is enabled, and the modal drops that source on servers too old to have it.
    const myMediaFooter = _jsx("div", { className: "uxpcore_mediapicker__footer", children: _jsx(Button, { title: context.$L('uxp-core.media-picker.open-my-media'), rightIcon: "fal arrow-up-right-from-square", variant: "secondary", size: "small", onClick: () => { window.open('/view/user/my-media', '_blank', 'noopener'); } }) });
    const list = _jsxs("div", { className: "uxpcore_mediapicker__list", children: [error &&
                _jsx(NotificationBlock, { class: "uxpcore_mediapicker__notice", variant: "danger", mode: "compact", layout: "bordered", icon: true, message: error, action: _jsx(Button, { title: context.$L('uxp-core.text.try-again'), size: "small", variant: "secondary", onClick: () => load(0) }) }), hasTree && isMobile && props.treeOpen === undefined && _jsx(Button, { className: "uxpcore_mediapicker__treebutton", title: selectionLabel, leftIcon: selection.kind === 'tag' ? 'fal tag' : 'fal folder', variant: "secondary", size: "small", onClick: () => setTreeSheet(true) }), !hideFilters &&
                _jsxs("div", { className: "uxpcore_mediapicker__toolbar", children: [_jsx("div", { className: "uxpcore_mediapicker__search", children: _jsx(Input, { value: query, onChange: (v) => setQuery(v), placeholder: context.$L('uxp-core.media-picker.search-library'), prefix: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'search'] }), fullWidth: true }) }), queriedTypes.length > 1 &&
                            _jsx("div", { className: "uxpcore_mediapicker__chips", children: typeOptions.map(o => _jsx("button", { type: "button", className: classNames('uxpcore_mediapicker__chip', {
                                        'uxpcore_mediapicker__chip--selected': typeFilter === o.value,
                                    }), onClick: () => setTypeFilter(o.value), children: o.label }, o.value)) })] }), _jsxs("div", { className: "uxpcore_mediapicker__results", ref: resultsRef, onScroll: onResultsScroll, children: [_jsxs("div", { className: grid ? 'uxpcore_mediapicker__cardgrid' : 'uxpcore_mediapicker__cardlist', children: [items.map(renderItem), firstLoad && Array.from({ length: SKELETON_TILES }, (_, i) => (_jsx("div", { className: "uxpcore_mediapicker__card uxpcore_mediapicker__item--skeleton", children: _jsx(SkeletonLoader, { width: "100%", height: "100%" }) }, `skeleton-${i}`)))] }), !loading && !error && items.length === 0 && (filtered
                        ? _jsx("div", { className: "uxpcore_mediapicker__message", children: context.$L('uxp-core.media-picker.no-match') })
                        : _jsxs("div", { className: "uxpcore_mediapicker__empty", children: [_jsx("span", { className: "uxpcore_mediapicker__empty-icon", children: _jsx(FontAwesomeIconWrapper, { icon: ['fal', 'folder-open'] }) }), _jsx("div", { className: "uxpcore_mediapicker__empty-title", children: context.$L('uxp-core.media-picker.no-files-yet') }), _jsx("div", { className: "uxpcore_mediapicker__empty-hint", children: context.$L('uxp-core.media-picker.no-files-hint') })] }))] }), !hasTree && myMediaFooter] });
    if (!hasTree)
        return list;
    return _jsxs("div", { className: "uxpcore_mediapicker__library", children: [!isMobile && _jsxs("aside", { className: "uxpcore_mediapicker__librarytree", children: [_jsx("div", { className: "uxpcore_mediapicker__librarytree-scroll", children: tree }), myMediaFooter] }), list, isMobile && _jsx(MobileSheet, { isOpen: treeSheet, onClose: () => setTreeSheet(false), title: context.$L('uxp-core.media-library.folders'), children: _jsxs("div", { className: "uxpcore_mediapicker__treesheet", children: [tree, myMediaFooter] }) })] });
};
export default LibraryView;
